<template>
  <main class="container-1 terrain-templates">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <h2>{{ isEditing ? 'Editar plantilla' : 'Nueva plantilla' }}</h2>
    </div>
    <div class="container-body">
      <modal name="terrain-templates-preview" :width="820" height="90%" :adaptive="true" :scrollable="true">
        <div class="terrain-templates__preview">
          <header class="terrain-templates__preview-header">
            <h2>Vista previa de plantilla</h2>
            <button type="button" class="terrain-templates__icon-btn" aria-label="Cerrar vista previa" title="Cerrar" @click="closePreview">
              <font-awesome-icon icon="times" />
            </button>
          </header>
          <div class="terrain-templates__preview-body">
            <TerrainFormRenderer :definition="draft" :answers.sync="previewAnswers" mode="preview" sanitize-hidden-answers />
          </div>
        </div>
      </modal>

      <label class="terrain-templates__title">
        Título de la plantilla
        <input :value="draft.title" :disabled="inUse" @input="updateDraft({ ...draft, title: $event.target.value })">
      </label>
      <p v-if="titleError" class="terrain-templates__error" role="alert">{{ titleError }}</p>

      <div class="terrain-templates__save-bar">
        <button
          type="button"
          class="terrain-templates__save-button"
          :disabled="inUse || saveStatus === 'saving' || !hasUnsavedChanges"
          @click="onSave"
        >
          {{ saveStatus === 'saving' ? 'Guardando…' : 'Guardar plantilla' }}
        </button>
        <button
          type="button"
          class="terrain-templates__secondary-button"
          :disabled="!serverTemplateId || validateStatus === 'validating'"
          @click="onValidate"
        >
          {{ validateStatus === 'validating' ? 'Validando…' : 'Validar' }}
        </button>
        <button
          type="button"
          class="terrain-templates__secondary-button"
          :disabled="!serverTemplateId || publishStatus === 'publishing' || archived"
          @click="onPublishConfirm"
        >
          {{ publishStatus === 'publishing' ? 'Publicando…' : 'Publicar' }}
        </button>
        <button type="button" class="terrain-templates__secondary-button terrain-templates__preview-button" @click="openPreview">
          <font-awesome-icon icon="eye" /> Vista previa
        </button>
        <span v-if="saveStatus === 'saved'" class="terrain-templates__save-status terrain-templates__save-status--saved">Guardado</span>
        <span v-else-if="saveStatus === 'error'" class="terrain-templates__save-status terrain-templates__save-status--error" role="alert">{{ saveErrorMessage }}</span>
        <TerrainBadge v-if="latestPublishedVersion" type="success" :message="`Publicada v${latestPublishedVersion}`" />
        <TerrainBadge v-if="archived" type="danger" message="Eliminada" />
      </div>

      <p v-if="archived" class="terrain-templates__archived-notice" role="status">
        Esta plantilla está eliminada. Restáurela desde el listado de plantillas para poder publicar cambios.
      </p>
      <p v-if="inUse" class="terrain-templates__archived-notice" role="status">
        Esta plantilla ya está ocupada por una campaña y no se puede editar.
      </p>

      <p
        v-if="validateStatus === 'invalid' || validateStatus === 'valid'"
        class="terrain-templates__validation"
        :class="{ 'terrain-templates__validation--valid': validateStatus === 'valid' }"
        role="status"
      >
        <template v-if="validateStatus === 'valid'">La plantilla es publicable.</template>
        <template v-else>
          <strong>No es publicable:</strong>
          <ul>
            <li v-for="(fieldError, index) in validationErrors" :key="index">{{ fieldError.message }}</li>
          </ul>
        </template>
      </p>

      <fieldset class="terrain-templates__builder-lock" :disabled="inUse">
        <TerrainBuilder :value="draft" @input="updateDraft" />
      </fieldset>
    </div>
  </main>
</template>

<script>
import { mapGetters } from "vuex";
import Breadcrumb from "@/components/Breadcrumb.vue";
import TerrainBadge from "@/components/terrain/TerrainBadge.vue";
import TerrainBuilder from "@/components/terrain/builder/TerrainBuilder.vue";
import TerrainFormRenderer from "@/components/terrain/form/TerrainFormRenderer.vue";
import { createTerrainApi } from "@/services/terrain/api.js";

export default {
  name: "TerrainTemplates",
  components: { Breadcrumb, TerrainBadge, TerrainBuilder, TerrainFormRenderer },
  data() {
    return { titleError: "", previewAnswers: [] };
  },
  computed: {
    ...mapGetters("terrain", [
      "builderDraft", "hasUnsavedChanges", "saveStatus", "saveErrorMessage",
      "serverTemplateId", "latestPublishedVersion", "archived", "inUse",
      "validateStatus", "validationErrors", "publishStatus", "publishErrorMessage",
    ]),
    draft() { return this.builderDraft; },
    isEditing() { return Boolean(this.$route.params.templateId); },
    routesBread() {
      return [
        { name: "Terreno" },
        { name: "Plantillas", path: "/terreno/plantillas" },
        { name: this.isEditing ? "Editar" : "Nueva" },
      ];
    },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
  },
  created() { this.initialize(); },
  watch: {
    "$route.params.templateId"() { this.initialize(); },
  },
  methods: {
    async initialize() {
      this.titleError = "";
      const templateId = this.$route.params.templateId;
      if (!templateId) {
        this.$store.dispatch("terrain/resetBuilder");
        return;
      }
      try {
        await this.$store.dispatch("terrain/loadTemplate", { api: this.terrainApi, templateId });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo cargar la plantilla.", type: "error" });
        this.$router.replace({ name: "terrain-templates" });
      }
    },
    updateDraft(definition) { this.$store.dispatch("terrain/updateBuilderDraft", definition); },
    openPreview() { this.previewAnswers = []; this.$modal.show("terrain-templates-preview"); },
    closePreview() { this.$modal.hide("terrain-templates-preview"); },
    async onSave() {
      this.titleError = "";
      if (!this.draft.title || !this.draft.title.trim()) {
        this.titleError = "El título de la plantilla es obligatorio.";
        return;
      }
      try {
        await this.$store.dispatch("terrain/saveTemplate", {
          api: this.terrainApi,
          title: this.draft.title,
          description: this.draft.description,
        });
        this.$store.dispatch("toast/show", { message: "Plantilla guardada correctamente.", type: "success" });
        if (!this.isEditing && this.serverTemplateId) {
          this.$router.replace({ name: "terrain-templates-edit", params: { templateId: this.serverTemplateId } });
        }
      } catch (error) {
        this.$store.dispatch("toast/show", { message: this.saveErrorMessage || "No se pudo guardar la plantilla.", type: "error" });
      }
    },
    async onValidate() {
      if (!this.serverTemplateId) return;
      try {
        const result = await this.$store.dispatch("terrain/validateTemplate", { api: this.terrainApi });
        this.$store.dispatch("toast/show", {
          message: result.valid ? "La plantilla es publicable." : "La plantilla tiene errores pendientes. Revise el detalle.",
          type: result.valid ? "success" : "error",
        });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo validar la plantilla.", type: "error" });
      }
    },
    onPublishConfirm() {
      this.$modal.show("dialog", {
        text: "¿Publicar esta plantilla? Se creará una nueva versión inmutable.",
        buttons: [
          { title: "Cancelar" },
          { title: "Publicar", handler: () => { this.$modal.hide("dialog"); this.onPublish(); } },
        ],
      });
    },
    async onPublish() {
      if (!this.serverTemplateId) return;
      try {
        await this.$store.dispatch("terrain/publishTemplate", { api: this.terrainApi });
        this.$store.dispatch("toast/show", { message: "Plantilla publicada correctamente.", type: "success" });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: this.publishErrorMessage || "No se pudo publicar la plantilla.", type: "error" });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-templates .container-body {
  display: grid;
  gap: var(--terrain-space-xl);
  margin: 0 auto;
  max-width: 1100px;
  width: 95%;
}

.terrain-templates__title {
  display: grid;
  gap: var(--terrain-space-xs);
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);
}

.terrain-templates__title input {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

.terrain-templates__error {
  margin: 0;
  color: var(--color-danger);
}

.terrain-templates__save-bar {
  align-items: center;
  display: flex;
  gap: var(--terrain-space-md);
}

.terrain-templates__save-button {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}

.terrain-templates__secondary-button {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .5; }
}

.terrain-templates__save-status--saved { color: var(--color-success); }
.terrain-templates__save-status--error { color: var(--color-danger); }

.terrain-templates__validation {
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  border-radius: var(--terrain-radius-sm);
  background: var(--terrain-color-danger-light);
  color: var(--color-danger);
  font-size: var(--terrain-font-caption);

  ul { margin: var(--terrain-space-xs) 0 0; padding-left: var(--terrain-space-lg); }

  &--valid {
    background: var(--terrain-color-success-light);
    color: var(--color-success);
  }
}

.terrain-templates__archived-notice {
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  border-radius: var(--terrain-radius-sm);
  background: var(--terrain-color-danger-light);
  color: var(--color-danger);
  font-size: var(--terrain-font-caption);
}

.terrain-templates__builder-lock {
  border: 0;
  margin: 0;
  min-inline-size: 0;
  padding: 0;
}

.terrain-templates__preview-button {
  align-items: center;
  display: inline-flex;
  gap: var(--terrain-space-xs);
  justify-content: center;
  white-space: nowrap;
}

.terrain-templates__preview {
  background: var(--color-mist);
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  min-height: 0;
}

.terrain-templates__preview-header {
  align-items: center;
  background: var(--color-white);
  border-bottom: 1px solid var(--color-line);
  display: flex;
  gap: var(--terrain-space-md);
  justify-content: space-between;
  padding: var(--terrain-space-md) var(--terrain-space-lg);

  h2 { color: var(--color-navy); margin: 0; }
}

.terrain-templates__preview-body {
  min-height: 0;
  overflow-y: auto;
  padding: var(--terrain-space-lg);
}

.terrain-templates__icon-btn {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: 50%;
  color: var(--color-navy);
  cursor: pointer;
  display: flex;
  height: 2.25rem;
  width: 2.25rem;
  justify-content: center;
}
</style>
